import { Alert, Box, Button, Chip, Paper, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { Autorenew as GenerateIcon, Download as DownloadIcon } from "@mui/icons-material";
import type { Run } from "../../services/lensService";
import { type DecisionFilter, formatWhen, matchesFilters } from "./lensUtils";

const useStyles = makeStyles()((theme) => ({
    root: { padding: theme.spacing(3), marginBottom: theme.spacing(3) },
    counts: { display: "flex", gap: theme.spacing(1), alignItems: "center", flexWrap: "wrap", marginBottom: theme.spacing(2) },
    notAssessed: { color: "#777", borderColor: "#999", borderStyle: "dashed", fontWeight: 600 },
    note: { color: "#777" },
    alert: { marginTop: theme.spacing(2) },
}));

interface RunDecisionsProps {
    run: Run;
    // Decided in Word, where the document is: no marked-up copy here.
    isWordRun: boolean;
    isGenerating: boolean;
    onGenerate: () => void;
    onDownload: () => void;
    decisionFilter: DecisionFilter | null;
    onDecisionFilter: (decision: DecisionFilter | null) => void;
}

const DECISION_CHIPS: { value: DecisionFilter; label: string; color?: "success" | "error" }[] = [
    { value: "accepted", label: "accepted", color: "success" },
    { value: "declined", label: "declined", color: "error" },
    { value: "undecided", label: "undecided" },
    { value: "notassessed", label: "not assessed" },
];

// Deciding never rewrites the file: the copy is regenerated here, explicitly, and is stale until it is.
const RunDecisions = ({ run, isWordRun, isGenerating, onGenerate, onDownload, decisionFilter, onDecisionFilter }: RunDecisionsProps) => {
    const { classes } = useStyles();
    const { decisions } = run;
    const countOf = (value: DecisionFilter) => run.findings.filter((finding) => matchesFilters(finding, null, value)).length;
    const isStale = run.redlineStale;
    const hasCopy = run.hasRedline;
    const redlineStatus = run.redline.status;
    const because = run.redline.error?.trim() ? `: ${run.redline.error.trim().replace(/\.$/, "")}.` : ".";

    return (
        <Paper variant="outlined" className={classes.root}>
            <Box className={classes.counts}>
                {DECISION_CHIPS.map(({ value, label, color }) => {
                    const count = countOf(value);
                    if (value === "notassessed" && count === 0) return null;
                    const active = decisionFilter === value;
                    return (
                        <Chip
                            key={value}
                            size="small"
                            clickable
                            variant={active ? "filled" : "outlined"}
                            color={color}
                            className={value === "notassessed" && !active ? classes.notAssessed : undefined}
                            label={`${count} ${label}`}
                            aria-pressed={active}
                            onClick={() => onDecisionFilter(active ? null : value)}
                        />
                    );
                })}
                {!isWordRun && (
                    <Button
                        size="small"
                        variant={isStale || !hasCopy ? "contained" : "outlined"}
                        startIcon={<GenerateIcon />}
                        onClick={onGenerate}
                        disabled={isGenerating}
                    >
                        {isGenerating ? "Generating…" : hasCopy ? "Regenerate marked-up copy" : "Generate marked-up copy"}
                    </Button>
                )}
                {hasCopy && (
                    <Button size="small" variant={isStale && !isWordRun ? "text" : "contained"} startIcon={<DownloadIcon />} onClick={onDownload}>
                        Download
                    </Button>
                )}
            </Box>

            {isWordRun ? (
                <Typography variant="body2" className={classes.note}>
                    These findings were decided in Word. An accepted finding was written into the client&rsquo;s document as a
                    tracked change with its comment; a declined one left the document unchanged.
                    {!run.hasOriginal
                        ? " The document was opened from CaseNest and is kept there, so Lens holds no copy."
                        : hasCopy
                          ? " The marked-up copy is the document as last saved to Lens from Word."
                          : " No copy has been saved to Lens from Word yet."}
                </Typography>
            ) : (
                <Typography variant="body2" className={classes.note}>
                    An accepted finding is written in as a tracked change and leaves its comment. A declined one writes nothing,
                    not even its comment; the decision stays on the record here. An undecided one is written in as if accepted.
                    {decisions.notAssessed > 0 &&
                        " A section marked not assessed matched no provision in the playbook, so there is no amendment to accept or decline — it leaves a comment, and it needs reading by hand."}
                </Typography>
            )}

            {isWordRun && isStale && (
                <Alert severity="info" className={classes.alert}>
                    This copy was saved from Word{run.redlineOn ? ` on ${formatWhen(run.redlineOn)}` : ""}. Decisions made since then are
                    in the colleague&rsquo;s document and will be here when it is next saved to Lens.
                </Alert>
            )}

            {!isWordRun && isStale && (
                <Alert severity="warning" className={classes.alert}>
                    Decisions have changed since this copy was generated
                    {run.redlineOn ? ` on ${formatWhen(run.redlineOn)}` : ""}. Generate it again before you send it — the file as
                    it stands does not match the decisions above.
                </Alert>
            )}

            {!isWordRun && hasCopy && redlineStatus === "commentsonly" && (
                <Alert severity="info" className={classes.alert}>
                    The tracked changes could not be written into this copy{because} It carries the comments only.
                </Alert>
            )}

            {!isWordRun && !hasCopy && redlineStatus !== "failed" && (
                <Alert severity="info" className={classes.alert}>
                    No marked-up copy has been produced for this run yet.
                </Alert>
            )}

            {!isWordRun && !hasCopy && redlineStatus === "failed" && (
                <Alert severity="error" className={classes.alert}>
                    The marked-up copy could not be produced{because} The findings below are unaffected — generate it again to
                    retry.
                </Alert>
            )}

            {!isWordRun && hasCopy && redlineStatus === "failed" && (
                <Alert severity="warning" className={classes.alert}>
                    The last attempt to regenerate the marked-up copy failed{because} The copy offered is the earlier one.
                </Alert>
            )}
        </Paper>
    );
};

export default RunDecisions;
